import React, { useMemo } from "react";
import "./CriticalFacilityMetadataView.css";

const humanize = (value) => String(value || "")
  .replace(/_/g, " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const displayValue = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const parseHstore = (rawValue) => {
  const value = String(rawValue || "");
  const entries = {};
  const pairPattern = /"((?:\\.|[^"])*)"\s*=>\s*"((?:\\.|[^"])*)"/g;
  let match;

  while ((match = pairPattern.exec(value)) !== null) {
    const unescape = (part) => part.replace(/\\"/g, '"').replace(/\\\\/g, "\\");
    entries[unescape(match[1])] = unescape(match[2]);
  }

  return entries;
};

const parseFacilities = (rawValue) => {
  let value = rawValue;
  try {
    for (let attempt = 0; attempt < 2 && typeof value === "string"; attempt += 1) {
      value = JSON.parse(value);
    }
  } catch {
    return { facilities: [], error: "Could not parse critical-facility metadata." };
  }

  const facilities = Array.isArray(value) ? value : value && typeof value === "object" ? [value] : [];
  if (!facilities.length) {
    return { facilities: [], error: value == null || value === "" ? "" : "No facility matches were found." };
  }
  return { facilities, error: "" };
};

const omittedFacilityKeys = new Set([
  "name",
  "facility_type",
  "osm_id",
  "osm_type",
  "parcel_match_method",
  "match_distance_metres",
  "tags",
]);

function MetadataRows({ entries }) {
  return Object.entries(entries || {})
    .filter(([, value]) => value !== "" && value != null)
    .map(([key, value]) => (
      <div className="mb-criticalMetadata-row" key={key}>
        <span>{humanize(key)}</span>
        <strong>{displayValue(value)}</strong>
      </div>
    ));
}

export default function CriticalFacilityMetadataView({ value }) {
  const parsed = useMemo(() => parseFacilities(value), [value]);

  if (parsed.error && !parsed.facilities.length) {
    return (
      <div className="mb-criticalMetadata-fallback">
        <div className="mb-criticalMetadata-warning">{parsed.error}</div>
        <pre>{displayValue(value)}</pre>
      </div>
    );
  }

  return (
    <div className="mb-criticalMetadata">
      <div className="mb-criticalMetadata-count">
        {parsed.facilities.length} facility match{parsed.facilities.length === 1 ? "" : "es"}
      </div>
      <div className="mb-criticalMetadata-list">
        {parsed.facilities.map((facility, index) => {
          const tags = facility?.tags && typeof facility.tags === "object" ? facility.tags : {};
          const otherTags = parseHstore(tags?.other_tags);
          const name = facility?.name || tags?.name || `Facility ${index + 1}`;
          const facilityType = facility?.facility_type || tags?.amenity || "";
          const osmId = facility?.osm_id || tags?.osm_way_id || tags?.osm_node_id || "";
          const osmType = facility?.osm_type || (tags?.osm_way_id ? "way" : tags?.osm_node_id ? "node" : "");
          const operator = tags?.operator || otherTags?.operator || "";
          const distanceRaw = facility?.match_distance_metres;
          const distance = distanceRaw == null || distanceRaw === "" ? NaN : Number(distanceRaw);
          const additionalFacilityFields = Object.fromEntries(
            Object.entries(facility || {}).filter(([key]) => !omittedFacilityKeys.has(key))
          );
          const additionalTags = Object.fromEntries(
            Object.entries({ ...tags, ...otherTags }).filter(([key]) => (
              !["name", "amenity", "operator", "other_tags"].includes(key)
            ))
          );

          return (
            <article className="mb-criticalMetadata-card" key={`${osmType}-${osmId}-${index}`}>
              <div className="mb-criticalMetadata-name">{name}</div>
              <div className="mb-criticalMetadata-summary">
                {facilityType ? <div><span>Type</span><strong>{humanize(facilityType)}</strong></div> : null}
                {osmId ? <div><span>OSM feature</span><strong>{`${humanize(osmType)} ${osmId}`.trim()}</strong></div> : null}
                {facility?.parcel_match_method ? (
                  <div><span>Match method</span><strong>{humanize(facility.parcel_match_method)}</strong></div>
                ) : null}
                {Number.isFinite(distance) ? (
                  <div><span>Match distance</span><strong>{distance.toFixed(1)} m</strong></div>
                ) : null}
                {operator ? <div><span>Operator</span><strong>{operator}</strong></div> : null}
              </div>
              {(Object.keys(additionalFacilityFields).length || Object.keys(additionalTags).length) ? (
                <details className="mb-criticalMetadata-details">
                  <summary>More OSM details</summary>
                  {Object.keys(additionalFacilityFields).length ? (
                    <div className="mb-criticalMetadata-detailGroup">
                      <div className="mb-criticalMetadata-detailTitle">Match metadata</div>
                      <MetadataRows entries={additionalFacilityFields} />
                    </div>
                  ) : null}
                  {Object.keys(additionalTags).length ? (
                    <div className="mb-criticalMetadata-detailGroup">
                      <div className="mb-criticalMetadata-detailTitle">OSM tags</div>
                      <MetadataRows entries={additionalTags} />
                    </div>
                  ) : null}
                </details>
              ) : null}
            </article>
          );
        })}
      </div>
    </div>
  );
}
